import styled from '@emotion/styled';
import { palette, typography } from './theme';

/**
 * Legacy `font` object — kept for backwards compatibility, now backed by the
 * new Lobbero type + colour tokens (see theme.ts). New code should prefer
 * `typography` / `palette` from theme.ts directly.
 */
export const font = {
  family: 'Inter',
  display: typography.display,
  weight: {
    light: typography.weight.light,
    normal: typography.weight.regular, // baseline bumped 300 → 400 for legibility
    medium: typography.weight.medium,
    semibold: typography.weight.semibold,
    bold: typography.weight.bold,
  },
  size: {
    xxs: typography.size.xxs, // 12
    xs: typography.size.sm, // 14
    s: typography.size.base, // 16
    m: typography.size.md, // 18
    l: typography.size.lg, // 20
    xl: typography.size.xl, // 24
    xxl: typography.size.xxl, // 30
  },
  color: {
    gray: palette.slate,
    grey: palette.slate,
    slate: palette.slate,
    ink: palette.ink,
    /* `purple` IS THE NAME OF A COLOUR THE PRODUCT NO LONGER HAS.
       It dates from the retired Deep Iris system (#8C67C9) and has resolved to
       brand teal since the re-brand — so a call site asking for "purple" was
       getting #00A19A, and on the dashboard's Logout label that measured 2.90:1
       on surfaceMuted across 23 screens. Both names now resolve to Deep Lagoon,
       which is what small coloured text on a light ground is supposed to be
       (design_system.md §2.1) and measures 4.9:1 on white.

       Kept as aliases rather than deleted: they are legacy call sites, and a
       rename that breaks the build teaches nothing. `lagoon` is the name new
       code should use. */
    purple: palette.lagoon,
    iris: palette.lagoon,
    lagoon: palette.lagoon,
    /* The full-strength brand hue, for SURFACES and large display text only —
       never for body-sized text on a light ground. */
    brand: palette.iris,
  },
};

export const Text = styled.p<{
  fontSize?: number;
  fontWeight?: number;
  fontFamily?: string;
  lineHeight?: string;
  color?: string;
  backgroundColour?: string;
  mt?: number;
  mb?: number;
  ml?: number;
  mr?: number;
  pt?: number;
  pb?: number;
  pr?: number;
  pl?: number;
  align?: string;
  width?: string;
  margin?: string;
  borderBottom?: string;
}>`
  display: flex;
  /* THE APP-WIDE OVERFLOW FLOOR, and both lines are load-bearing.
   *
   * Proven at runtime on 2026-08-10: /dashboard/users at 320px scrolled to
   * 502px because ONE of these carried a 67-character email. The element was
   * 396px wide inside a 222px parent and would not shrink.
   *
   * min-width: 0 removes the automatic minimum. A flex item defaults to
   * min-width: auto, whose floor is its MIN-CONTENT width — for an unbreakable
   * token that is the whole token, so the item overflows its parent rather than
   * wrapping, however narrow the parent gets.
   *
   * overflow-wrap: anywhere, NOT break-word, and the difference is the entire
   * fix. Both break a long word at paint time, but only anywhere counts toward
   * intrinsic sizing — so only anywhere actually collapses min-content and lets
   * the box narrow. break-word (the global default this app sets on body)
   * leaves min-content at the full token and would not have moved a pixel here.
   *
   * Scoped to Text rather than made global for the same reason: a table column
   * or a button given anywhere would be free to break words that had room to
   * fit. This is the primitive that carries arbitrary user data.
   *
   * NO BACKTICKS IN THIS COMMENT: it sits inside a styled template literal, and
   * one silently terminates the string. */
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${({ fontFamily }) => fontFamily || 'Inter'};
  font-weight: ${({ fontWeight }) => fontWeight || '400'};
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '16px')};
  line-height: ${({ lineHeight }) => lineHeight || '1.5'};
  letter-spacing: -0.011em;
  color: ${({ color }) => color || 'inherit'};
  background-color: ${({ backgroundColour }) =>
    backgroundColour || 'transparent'};
  margin-top: ${({ mt }) => mt && `${mt}px`};
  margin-bottom: ${({ mb }) => mb && `${mb}px`};
  margin-left: ${({ ml }) => ml && `${ml}px`};
  margin-right: ${({ mr }) => mr && `${mr}px`};
  padding-top: ${({ pt }) => pt && `${pt}px`};
  padding-bottom: ${({ pb }) => pb && `${pb}px`};
  padding-left: ${({ pl }) => pl && `${pl}px`};
  padding-right: ${({ pr }) => pr && `${pr}px`};
  text-align: ${({ align }) => align || 'left'};
  width: ${({ width }) => width && `${width}%`};
  margin: ${({ margin }) => margin && `${margin}`};
  border-bottom: ${({ borderBottom }) => borderBottom && `${borderBottom}`};
`;

export const EllipsisText = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 2; // Limit to two lines
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  max-height: 3em; // Adjust this value based on your line-height
  line-height: 1.5em; // Adjust this value based on the design
`;

/**
 * THE ONE VISUALLY-HIDDEN HELPER.
 *
 * WP-39's own cleanup item: this pattern existed twice, in two component
 * folders, and nowhere in `style-utils` — `VisuallyHidden` in
 * `iq/iq-chat.styled.tsx` and `SrOnly` in the analytics `chart.styled.tsx`,
 * which had already drifted (only one of them carried `clip-path`). It belongs
 * here, beside `Text`, because it is a typography primitive rather than a
 * feature's private styling.
 *
 * `clip` is deprecated and is the only form pre-Safari-14.1 honours; the modern
 * `clip-path` wins wherever both are understood, so both are declared.
 *
 * NOT `display: none` and NOT `visibility: hidden`: either would take the
 * content out of the accessibility tree, which is the opposite of the point.
 * The 1x1 box is also why the responsive harness excuses this shape by pattern
 * rather than by class name — see `scripts/analytics/responsive.js`.
 */
export const VisuallyHidden = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;
